import { useEffect, useState } from 'react';
import CustomModal from '@/components/CustomModal/CustomModal';
import CancelSubscriptionModal from '@/components/CustomModal/CancelSubscriptionModal';
import Plans from '@/components/Plans/Plans';
import { useAuthStore } from '@/stores/authStore';
import { useCreateCheckoutSession, useDowngradeAtPeriodEnd } from '@/services/noteGoApi/noteGoApiComponents';
import { getFreePlanProductId, initializePricingCache } from '@/services/subscriptionService';
import { useNavigate } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';
import { AccountStatusTypes } from '@/helpers/accountStatus';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';
import { rememberCheckoutPrice } from '@/utils/checkoutPrice';

const PlansModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const { mutate: createCheckoutSession } = useCreateCheckoutSession();
  const { mutate: downgradeSubscription } = useDowngradeAtPeriodEnd();
  const [freePlanProductId, setFreePlanProductId] = useState<string | null>(null);
  const [selectedDowngradeProductId, setSelectedDowngradeProductId] = useState<string | null>(null);
  const { userToken, accountStatus, userData, fetchAndSetUserData } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const showAlert = useAlertStore((state) => state.showAlert);

  useEffect(() => {
    const initPricing = async () => {
      try {
        await initializePricingCache();
        const freeId = await getFreePlanProductId();
        setFreePlanProductId(freeId);
      } catch (err) {
        errorReporter.expected('Failed to load free plan product ID', err, {
          feature: 'subscription',
          operation: 'loadFreePlanId',
        });
      }
    };
    initPricing();
  }, []);

  useEffect(() => {
    const modalClosedBefore = localStorage.getItem('upgradeModalClosed');
    const expiredAccount = accountStatus === AccountStatusTypes.EXPIRED;

    if (modalClosedBefore === 'true' && !expiredAccount) {
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
  }, [accountStatus]);

  const handleClose = () => {
    if (accountStatus === AccountStatusTypes.EXPIRED) return;
    setIsModalOpen(false);
    localStorage.setItem('upgradeModalClosed', 'true'); // Save the state
  };

  // Open downgrade confirmation modal when user selects FREE plan.
  // Capture the product_id of the plan the user actually selected so the
  // confirmed downgrade targets that plan, not a generic/stale free plan.
  const handleDowngradeToFree = (productId?: string) => {
    setSelectedDowngradeProductId(productId ?? null);
    setIsDowngradeModalOpen(true);
  };

  // Handle confirmed downgrade to free plan
  const handleConfirmDowngrade = async () => {
    const targetProductId = selectedDowngradeProductId || freePlanProductId;
    if (!targetProductId) {
      showAlert('Unable to determine free plan. Please refresh the page and try again.', 'error');
      return;
    }

    setIsDowngrading(true);
    downgradeSubscription(
      {
        body: { product_id: targetProductId },
        headers: { authorization: `Bearer ${userToken}` },
      },
      {
        onSuccess: () => {
          showAlert('Your subscription has been downgraded to the Free plan.', 'success');
          setIsDowngradeModalOpen(false);
          setIsModalOpen(false);
          fetchAndSetUserData({ force: true });
        },
        onError: (err) => {
          errorReporter.critical('Failed to downgrade subscription', err, {
            feature: 'stripe',
            operation: 'downgradeSubscription',
          });
          showAlert('Failed to downgrade subscription. Please try again.', 'error');
        },
        onSettled: () => {
          setIsDowngrading(false);
        },
      },
    );
  };

  const handleUpgrade = (priceId: string, _subscriptionId?: string | null, stripeCustomerId?: string | null) => {
    setIsLoading(true);

    // Always route through Stripe checkout so users can enter or update their card
    const userEmail = userData?.email || null;

    createCheckoutSession(
      {
        body: {
          price_id: priceId,
          customer_id: stripeCustomerId,
          customer_email: userEmail,
        },
        headers: { authorization: `Bearer ${userToken}` },
      },
      {
        onSuccess: (response) => {
          rememberCheckoutPrice(response.session_id, priceId);
          navigate(`${RouteMap.CHECKOUT}/${response.client_secret}`);
          setIsLoading(false);
        },
        onError: (err) => {
          errorReporter.critical('Failed to create checkout session', err, {
            feature: 'stripe',
            operation: 'createCheckoutSession',
          });
          setIsLoading(false);
          showAlert('Failed to create checkout session. Please try again.', 'error');
        },
      },
    );
  };

  return (
    <>
      <CustomModal open={isModalOpen} handleClose={handleClose} boxSx={{ minWidth: 'max-content' }}>
        <Plans
          handleUpgrade={handleUpgrade}
          onContinue={handleClose}
          onDowngradeToFree={handleDowngradeToFree}
          currentUserPlan={userData?.subscription}
          isLoading={isLoading}
        />
      </CustomModal>

      <CancelSubscriptionModal
        open={isDowngradeModalOpen}
        handleClose={() => setIsDowngradeModalOpen(false)}
        text="Are you sure you want to downgrade to the Free plan? This takes effect immediately and your current plan's features will end now."
        confirmButtonText="Downgrade to Free"
        onConfirm={handleConfirmDowngrade}
        isLoading={isDowngrading}
      />
    </>
  );
};

export default PlansModal;
